import { bool, func, object, string } from 'prop-types';
import React from 'react';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';

import { formatMoney } from '../../util/currency';
import {
  ACCESS_MODE_SUBSCRIPTION,
  findSubscriptionPlan,
  getSubscriptionPlans,
  getTrialDays,
  getTrialEndDate,
  planAppliesTrial,
  subscriptionPlanLabelMessageId,
} from '../../util/digitalSubscription';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { sendTrackingEvent, TRACKING_EVENTS } from '../../analytics/trackingEvent';

import css from './CheckoutPage.module.css';

const { Money } = sdkTypes;

const SubscriptionCheckoutSection = props => {
  const { listing, selectedPlanId, onSelectPlan, intl } = props;
  const publicData = listing?.attributes?.publicData || {};
  if (publicData.accessMode !== ACCESS_MODE_SUBSCRIPTION) {
    return null;
  }

  const selected = findSubscriptionPlan(publicData, selectedPlanId);
  const hasTrial = planAppliesTrial(publicData, selected);
  const trialDays = getTrialDays(publicData);
  const trialEnds = getTrialEndDate(trialDays);
  const plans = getSubscriptionPlans(publicData, listing?.attributes?.price?.currency);

  return (
    <div className={css.subscriptionCheckout} data-testid="subscription-checkout-section">
      {hasTrial ? (
        <div className={css.subscriptionTrialCard} data-testid="subscription-trial-block">
          <div className={css.subscriptionTrialCopy}>
            <h2 className={css.subscriptionHeading} data-testid="subscription-trial-title">
              <FormattedMessage id="CheckoutPage.subscriptionTrialTitle" />
            </h2>
            <p className={css.subscriptionBody} data-testid="subscription-trial-body">
              <FormattedMessage
                id="CheckoutPage.subscriptionTrialBody"
                values={{
                  days: trialDays,
                  endDate: trialEnds
                    ? intl.formatDate(trialEnds, { day: 'numeric', month: 'short' })
                    : '',
                }}
              />
            </p>
          </div>
          <CalendarMonthOutlinedIcon
            className={css.subscriptionTrialIcon}
            data-testid="subscription-trial-calendar"
            fontSize="small"
            aria-hidden
          />
        </div>
      ) : null}

      <fieldset className={css.subscriptionPlanGroup}>
        <legend className={css.subscriptionHeading} data-testid="subscription-plan-legend">
          <FormattedMessage
            id={hasTrial ? 'CheckoutPage.afterYourTrial' : 'CheckoutPage.choosePlan'}
          />
        </legend>
        {plans.map(plan => {
          const price = formatMoney(intl, new Money(plan.amount, plan.currency));
          const checked = selected?.id === plan.id;
          return (
            <label
              key={plan.id}
              className={checked ? css.subscriptionPlanOptionSelected : css.subscriptionPlanOption}
              data-testid={`subscription-plan-${plan.interval}`}
            >
              <input
                type="radio"
                name="subscriptionPlanId"
                value={plan.id}
                checked={checked}
                onChange={() => {
                  onSelectPlan(plan.id);
                  sendTrackingEvent({
                    name: TRACKING_EVENTS.SUBSCRIPTION_PLAN_SELECTED,
                    parameters: {
                      plan_id: plan.id,
                      plan_interval: plan.interval,
                      access_mode: ACCESS_MODE_SUBSCRIPTION,
                    },
                  });
                }}
              />
              <span className={css.subscriptionPlanCopy}>
                <span className={css.subscriptionPlanText}>
                  <span className={css.subscriptionPlanLabel}>
                    <FormattedMessage id={subscriptionPlanLabelMessageId(plan.interval)} />
                  </span>
                  {plan.description ? (
                    <span className={css.subscriptionPlanDescription}>{plan.description}</span>
                  ) : null}
                </span>{' '}
                <span
                  className={css.subscriptionPlanPrice}
                  data-testid={`subscription-plan-${plan.interval}-price`}
                >
                  {price}
                </span>
              </span>
            </label>
          );
        })}
      </fieldset>
    </div>
  );
};

SubscriptionCheckoutSection.propTypes = {
  listing: object,
  selectedPlanId: string,
  onSelectPlan: func.isRequired,
  intl: intlShape.isRequired,
  hasTrial: bool,
};

export default SubscriptionCheckoutSection;
